import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { financialReportsAPI, type GeneralLedger } from '../../services/api';

interface GeneralLedgerReportProps {
  siteId: string;
  dateRange: { startDate: string; endDate: string };
}

const GeneralLedgerReport: React.FC<GeneralLedgerReportProps> = ({ siteId, dateRange }) => {
  const [page, setPage] = useState(1);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');

  const { data, isLoading, error } = useQuery(
    ['general-ledger', siteId, dateRange, page, selectedAccountId],
    () => financialReportsAPI.getGeneralLedger(siteId, { 
      ...dateRange, 
      page,
      accountId: selectedAccountId || undefined 
    }),
    { keepPreviousData: true }
  );

  const generalLedgerData = data?.data;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Error loading general ledger</p>
      </div>
    );
  }

  if (!generalLedgerData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No data available for the selected period</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">General Ledger</h3>
          <p className="text-sm text-gray-500">
            {new Date(generalLedgerData.period.startDate).toLocaleDateString()} - {new Date(generalLedgerData.period.endDate).toLocaleDateString()}
          </p>
        </div>
        {generalLedgerData.openingBalance !== 0 && (
          <div className="text-right">
            <p className="text-sm text-gray-500">Opening Balance</p>
            <p className="text-lg font-semibold">
              {formatCurrency(generalLedgerData.openingBalance)}
            </p>
          </div>
        )}
      </div>

      {/* Journal Entries Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Debit
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Posted By
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {generalLedgerData.entries.map((entry) => (
                entry.lines.map((line, lineIndex) => (
                  <tr key={`${entry.id}-${lineIndex}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(entry.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.reference || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {entry.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <span className="font-medium">{line.accountName}</span>
                        <span className="text-xs text-gray-500 ml-2">{line.accountCode}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {line.debit > 0 ? formatCurrency(line.debit) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {line.credit > 0 ? formatCurrency(line.credit) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {entry.postedBy || '-'}
                    </td>
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {generalLedgerData.pagination.pages > 1 && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Showing {((generalLedgerData.pagination.page - 1) * generalLedgerData.pagination.limit) + 1} to{' '}
            {Math.min(generalLedgerData.pagination.page * generalLedgerData.pagination.limit, generalLedgerData.pagination.total)} of{' '}
            {generalLedgerData.pagination.total} entries
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(prev => Math.max(prev - 1, 1))}
              disabled={generalLedgerData.pagination.page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-1">
              Page {generalLedgerData.pagination.page} of {generalLedgerData.pagination.pages}
            </span>
            <button
              onClick={() => setPage(prev => Math.min(prev + 1, generalLedgerData.pagination.pages))}
              disabled={generalLedgerData.pagination.page === generalLedgerData.pagination.pages}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralLedgerReport;
